/**
 * Special / built-in action IDs that the editor treats with custom logic
 * (initial step, output step, foreach iteration markers, etc.).
 *
 * NEVER hardcode these strings elsewhere. Always reference SPECIAL_ACTIONS.
 */
export const SPECIAL_ACTIONS = {
  initial: 'initial',
  output: 'output',
  route: 'route',
  startIteration: 'startIteration',
  endIteration: 'endIteration',
  forEachPdfFile: 'forEachPdfFile',
} as const;

export type SpecialActionId = (typeof SPECIAL_ACTIONS)[keyof typeof SPECIAL_ACTIONS];

/**
 * Action IDs that the editor renders + treats as built-in nodes rather than
 * user-addable steps. Internal nodes never appear in the action palette.
 */
export const INTERNAL_ACTION_IDS: ReadonlySet<string> = new Set<string>([
  SPECIAL_ACTIONS.initial,
  SPECIAL_ACTIONS.output,
  SPECIAL_ACTIONS.startIteration,
  SPECIAL_ACTIONS.endIteration,
]);

export const isInternalAction = (actionId: string): boolean => INTERNAL_ACTION_IDS.has(actionId);

/** `forEach*` prefix matcher — covers forEachPdfFile, forEachX, etc. */
export const isForEachAction = (actionId: string): boolean => actionId.startsWith('forEach');

/**
 * The "anchor" steps that bound a workflow and cannot be deleted by the user
 * (initial + output + the iteration markers inside a forEach).
 */
export const isAnchorAction = (actionId: string): boolean =>
  actionId === SPECIAL_ACTIONS.initial ||
  actionId === SPECIAL_ACTIONS.output ||
  actionId === SPECIAL_ACTIONS.startIteration ||
  actionId === SPECIAL_ACTIONS.endIteration;

/**
 * The two outermost anchors (initial + output). Convenience predicate used
 * in several places that only care about workflow endpoints.
 */
export const isWorkflowEndpoint = (actionId: string): boolean =>
  actionId === SPECIAL_ACTIONS.initial || actionId === SPECIAL_ACTIONS.output;
